import { useTranslate } from '@tolgee/react'
import { ConfirmationDialog } from '@tom-ui/ui'
import { appStore, MAX_INT32_VALUE } from '@tom-ui/utils'
import { ConfigAreaType } from '@yard/app/api'
import { getConfigAreaByType } from '@yard/utils/yard-block.util'
import { observer } from 'mobx-react-lite'
import { useYardSetupMessageHandler } from '../../hooks/useYardSetupMessageHandler'
import { ILimitStackHeightConfigAreaFormData } from '../../models/yard-setup.model'
import {
  YardBlockEditSectionDialogType,
  YardBlockViewStore,
} from '../../stores/nodes/yard-block.view-store'
import { LimitStackHeightConfigAreaForm } from './LimitStackHeightConfigAreaForm'

interface Props {
  yardBlockViewStore: YardBlockViewStore
}

export const YardBlockAdjustMaxTierDialog = observer(({ yardBlockViewStore }: Props) => {
  const { t } = useTranslate()
  const { getExceptionMessage, getBlockSuccessMessage } = useYardSetupMessageHandler()

  const currentMaxStackHeight = (() => {
    if (!yardBlockViewStore.focusedYardBlock) return

    if (yardBlockViewStore.limitStackHeightDialogMode === YardBlockEditSectionDialogType.Bay) {
      if (!yardBlockViewStore.editingBayIndex) return

      const currentNode = getConfigAreaByType(
        ConfigAreaType.MaxStackHeight,
        yardBlockViewStore.focusedYardBlock.configAreas,
        yardBlockViewStore.editingBayIndex,
        yardBlockViewStore.editingBayIndex,
        1,
        MAX_INT32_VALUE,
      )

      return currentNode?.stackHeight
    }

    if (yardBlockViewStore.limitStackHeightDialogMode === YardBlockEditSectionDialogType.Row) {
      if (!yardBlockViewStore.editingRowIndex) return

      const currentNode = getConfigAreaByType(
        ConfigAreaType.MaxStackHeight,
        yardBlockViewStore.focusedYardBlock.configAreas,
        1,
        MAX_INT32_VALUE,
        yardBlockViewStore.editingRowIndex,
        yardBlockViewStore.editingRowIndex,
      )

      return currentNode?.stackHeight
    }

    if (yardBlockViewStore.limitStackHeightDialogMode === YardBlockEditSectionDialogType.Slot) {
      if (!yardBlockViewStore.editingBayIndex || !yardBlockViewStore.editingRowIndex) return

      const currentNode = getConfigAreaByType(
        ConfigAreaType.MaxStackHeight,
        yardBlockViewStore.focusedYardBlock.configAreas,
        yardBlockViewStore.editingBayIndex,
        yardBlockViewStore.editingBayIndex,
        yardBlockViewStore.editingRowIndex,
        yardBlockViewStore.editingRowIndex,
      )

      return currentNode?.stackHeight
    }

    return undefined
  })()

  const onSubmitLimitStackHeight = async (data: ILimitStackHeightConfigAreaFormData) => {
    try {
      switch (yardBlockViewStore.limitStackHeightDialogMode) {
        case YardBlockEditSectionDialogType.Bay:
          await yardBlockViewStore.limitCurrentBayStackHeightConfigArea(data)
          break
        case YardBlockEditSectionDialogType.Row:
          await yardBlockViewStore.limitCurrentRowStackHeightConfigArea(data)
          break
        case YardBlockEditSectionDialogType.Slot:
          await yardBlockViewStore.limitCurrentSlotStackHeightConfigArea(data)
          break
      }

      appStore.setShowAlert('success', getBlockSuccessMessage(!!yardBlockViewStore.editingId))
      yardBlockViewStore.closeLimitStackHeightDialog()
    } catch (e: any) {
      appStore.setShowAlert('error', getExceptionMessage(e))
    }
  }

  const onRemoveLimitStackHeight = async () => {
    try {
      switch (yardBlockViewStore.limitStackHeightDialogMode) {
        case YardBlockEditSectionDialogType.Bay:
          await yardBlockViewStore.removeCurrentBayMaxStackHeightConfigArea()
          break
        case YardBlockEditSectionDialogType.Row:
          await yardBlockViewStore.removeCurrentRowMaxStackHeightConfigArea()
          break
        case YardBlockEditSectionDialogType.Slot:
          await yardBlockViewStore.removeCurrentSlotMaxStackHeightConfigArea()
          break
      }

      appStore.setShowAlert('success', getBlockSuccessMessage(!!yardBlockViewStore.editingId))
      yardBlockViewStore.closeLimitStackHeightDialog()
    } catch (e: any) {
      appStore.setShowAlert('error', getExceptionMessage(e))
    }
  }

  return (
    <ConfirmationDialog
      open={yardBlockViewStore.isLimitStackHeightDialogOpen}
      title={t('maxStackHeight', 'Max stack height')}
      primaryActionText={t('save', 'Save')}
      secondaryActionText={t('removeLimit', 'Remove limit')}
      closeLabel={t('cancel', 'cancel')}
      formId='limit-stack-height-config-area-form'
      onClose={() => yardBlockViewStore.closeLimitStackHeightDialog()}
      onConfirm={() => {
        return
      }}
      onSecondaryAction={
        currentMaxStackHeight
          ? () => {
              onRemoveLimitStackHeight()
            }
          : undefined
      }
      maxWidth='xs'
    >
      <LimitStackHeightConfigAreaForm
        id='limit-stack-height-config-area-form'
        maxStackHeight={yardBlockViewStore.focusedYardBlock?.maxTiers}
        currentStackHeight={currentMaxStackHeight}
        onSubmit={onSubmitLimitStackHeight}
      />
    </ConfirmationDialog>
  )
})
